/**
 * 라이브(공연) 관련 Write 작업 목차
 * 
 * - createLive() - 라이브 생성 (자동 이미지 압축)
 * - updateLive() - 라이브 수정 (자동 이미지 압축)
 * - deleteLive() - 라이브 삭제
 * - toggleLiveApproval() - 승인 상태 토글
 * - toggleLiveBanner() - 배너 상태 토글
 * - toggleLiveAboutReference() - 서비스 안내(/about) 레퍼런스 노출 토글
 * - saveNewArtists() - 신규 아티스트 자동 저장
 */

import apiClient from "@/utils/api";
import { collection, query, where, getDocs } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";
import { compressBannerImage, compressImage } from "@/utils/imageCompressor";

/**
 * 공연 제목으로 URL slug 생성
 * @param {string} title - 공연 제목
 * @returns {string} slug (예: "서울재즈페스티벌-lx3k9")
 */
export function generateLiveSlug(title) {
  const sanitized = (title || "live")
    .trim()
    .replace(/[^\w\s가-힣ㄱ-ㅎㅏ-ㅣ]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 40);
  const suffix = Date.now().toString(36).slice(-5);
  return `${sanitized}-${suffix}`;
}

/**
 * 라이브 생성 (자동 이미지 압축)
 * @param {Object} liveData - 라이브 데이터
 * @param {File} posterFile - 포스터 파일
 * @param {File} bannerFile - 배너 파일 (선택)
 * @param {File} detailImageFile - 상세 이미지 파일 (선택)
 * @param {File} detailImage2File - 상세 이미지 2 파일 (선택)
 * @param {File} detailImage3File - 상세 이미지 3 파일 (선택)
 * @param {File} detailImage4File - 상세 이미지 4 파일 (선택)
 * @returns {Promise<string>} 생성된 라이브 ID
 */
export async function createLive(
  liveData,
  posterFile,
  bannerFile,
  detailImageFile,
  detailImage2File,
  detailImage3File,
  detailImage4File
) {
  try {
    const formData = new FormData();

    // slug 없으면 자동 생성 (제목 기반)
    const dataWithSlug = {
      ...liveData,
      slug: liveData.slug || generateLiveSlug(liveData.title),
    };
    
    // 라이브 데이터를 JSON 문자열로 추가 (서버에서 'data' 필드로 받음)
    formData.append("data", JSON.stringify(dataWithSlug));
    
    // 이미지 압축 후 파일 추가
    if (posterFile) {
      const compressedPoster = await compressImage(posterFile);
      formData.append("poster", compressedPoster);
    }
    
    if (bannerFile) {
      const compressedBanner = await compressBannerImage(bannerFile);
      formData.append("banner", compressedBanner);
    }
    
    if (detailImageFile) {
      const compressedDetailImage = await compressImage(detailImageFile);
      formData.append("detailImage", compressedDetailImage);
    }
    
    if (detailImage2File) {
      const compressedDetailImage2 = await compressImage(detailImage2File);
      formData.append("detailImage2", compressedDetailImage2);
    }
    if (detailImage3File) {
      const compressedDetailImage3 = await compressImage(detailImage3File);
      formData.append("detailImage3", compressedDetailImage3);
    }
    if (detailImage4File) {
      const compressedDetailImage4 = await compressImage(detailImage4File);
      formData.append("detailImage4", compressedDetailImage4);
    }

    const response = await apiClient.post("/api/live", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const liveId = response.data.data.id;

    // 라이브 등록 활동 기록
    trackActivity(ACTIVITY_TYPES.LIVE_CREATE, {
      target: "live",
      targetId: liveId,
      metadata: { title: liveData.title },
    });

    return liveId;
  } catch (error) {
    console.error("라이브 생성 오류:", error);
    throw error;
  }
}

/**
 * 라이브 수정 (자동 이미지 압축)
 * @param {string} liveId - 라이브 ID
 * @param {Object} updateData - 수정할 데이터
 * @param {File} posterFile - 포스터 파일 (선택)
 * @param {File} bannerFile - 배너 파일 (선택)
 * @param {File} detailImageFile - 상세 이미지 파일 (선택)
 * @param {File} detailImage2File - 상세 이미지 2 파일 (선택)
 * @param {File} detailImage3File - 상세 이미지 3 파일 (선택)
 * @param {File} detailImage4File - 상세 이미지 4 파일 (선택)
 * @returns {Promise<void>}
 */
export async function updateLive(
  liveId,
  updateData,
  posterFile,
  bannerFile,
  detailImageFile,
  detailImage2File,
  detailImage3File,
  detailImage4File
) {
  try {
    const formData = new FormData();

    // 수정 시에도 slug가 없으면 제목 기반으로 생성 (기존 slug 유지 우선)
    const dataWithSlug = {
      ...updateData,
      slug: updateData.slug || generateLiveSlug(updateData.title),
    };
    
    // 라이브 데이터를 JSON 문자열로 추가 (서버에서 'data' 필드로 받음)
    formData.append("data", JSON.stringify(dataWithSlug));
    
    // 이미지 압축 후 파일 추가
    if (posterFile) {
      const compressedPoster = await compressImage(posterFile);
      formData.append("poster", compressedPoster);
    }
    
    if (bannerFile) {
      const compressedBanner = await compressBannerImage(bannerFile);
      formData.append("banner", compressedBanner);
    }
    
    if (detailImageFile) {
      const compressedDetailImage = await compressImage(detailImageFile);
      formData.append("detailImage", compressedDetailImage);
    }
    
    if (detailImage2File) {
      const compressedDetailImage2 = await compressImage(detailImage2File);
      formData.append("detailImage2", compressedDetailImage2);
    }
    if (detailImage3File) {
      const compressedDetailImage3 = await compressImage(detailImage3File);
      formData.append("detailImage3", compressedDetailImage3);
    }
    if (detailImage4File) {
      const compressedDetailImage4 = await compressImage(detailImage4File);
      formData.append("detailImage4", compressedDetailImage4);
    }

    await apiClient.put(`/api/live/${liveId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // 라이브 수정 활동 기록
    trackActivity(ACTIVITY_TYPES.LIVE_UPDATE, {
      target: "live",
      targetId: liveId,
      metadata: { title: updateData.title },
    });
  } catch (error) {
    console.error("라이브 수정 오류:", error);
    throw error;
  }
}

/**
 * 라이브 삭제
 * @param {string} liveId - 라이브 ID
 * @returns {Promise<void>}
 */
export async function deleteLive(liveId) {
  try {
    await apiClient.delete(`/api/live/${liveId}`);
  } catch (error) {
    console.error("라이브 삭제 오류:", error);
    throw error;
  }
}

/**
 * 라이브 승인 상태 토글
 * @param {string} liveId - 라이브 ID
 * @param {boolean} currentStatus - 현재 승인 상태
 * @returns {Promise<boolean>} 새로운 승인 상태
 */
export async function toggleLiveApproval(liveId, currentStatus) {
  try {
    const newStatus = !currentStatus;
    const response = await apiClient.patch(`/api/live/${liveId}/approval`, {
      approved: newStatus,
    });
    return response.data.data.approved;
  } catch (error) {
    console.error("승인 상태 변경 오류:", error);
    throw error;
  }
}

/**
 * 라이브 배너 상태 토글
 * @param {string} liveId - 라이브 ID
 * @param {boolean} currentStatus - 현재 배너 상태
 * @returns {Promise<boolean>} 새로운 배너 상태
 */
export async function toggleLiveBanner(liveId, currentStatus) {
  try {
    const newStatus = !currentStatus;
    const response = await apiClient.patch(`/api/live/${liveId}/banner`, {
      bannerOn: newStatus,
    });
    return response.data.data.bannerOn;
  } catch (error) {
    console.error("배너 상태 변경 오류:", error);
    throw error;
  }
}

/**
 * 서비스 안내 페이지(/about) 레퍼런스 공연 노출 여부 (관리자 전용, 백엔드 API)
 * @param {string} liveId - 라이브 문서 ID
 * @param {boolean} currentValue - 현재 aboutReference 값
 * @returns {Promise<boolean>} 변경 후 값 (`response.data.data.aboutReference`)
 */
export async function toggleLiveAboutReference(liveId, currentValue) {
  try {
    const newValue = !currentValue;
    const response = await apiClient.patch(`/api/live/${liveId}/about-reference`, {
      aboutReference: newValue,
    });
    const returned = response.data?.data?.aboutReference;
    return typeof returned === "boolean" ? returned : newValue;
  } catch (error) {
    console.error("안내 레퍼런스 변경 오류:", error);
    throw error;
  }
}

/**
 * 신규 아티스트를 artists 컬렉션에 자동 저장
 * @param {Array<string>} artistNames - 아티스트 이름 배열
 * @param {string} type - 공연 타입
 * @returns {Promise<void>}
 */
export async function saveNewArtists(artistNames, type) {
  // 유효한 아티스트 이름만 필터링
  const validArtists = artistNames
    .filter(name => name && typeof name === "string" && name.trim())
    .map(name => name.trim());

  if (validArtists.length === 0) {
    return;
  }

  // 각 아티스트를 개별적으로 확인 후 생성
  for (const artistName of validArtists) {
    try {
      // Firebase에서 name 필드로 아티스트 존재 여부 확인 (read 작업)
      const artistsQuery = query(
        collection(firestore, "artists"),
        where("name", "==", artistName)
      );
      const querySnapshot = await getDocs(artistsQuery);

      // 이미 존재하면 스킵
      if (!querySnapshot.empty) {
        continue;
      }

      // 신규 아티스트만 API를 통해 생성 (write 작업)
      // 라이브 타입을 아티스트 타입 형식으로 변환
      let typeArray;
      let category;
      
      if (type === "band") {
        typeArray = ["band"];
        category = "band";
      } else if (type === "vocal") {
        typeArray = ["vocal"];
        category = "individual";
      } else if (type === "dance") {
        typeArray = ["dance"];
        category = "individual";
      } else if (type === "mix") {
        // mix는 여러 타입을 의미하므로 기본적으로 vocal로 설정
        typeArray = ["vocal"];
        category = "individual";
      } else {
        // 기본값
        typeArray = ["vocal"];
        category = "individual";
      }

      await apiClient.post("/api/artist", {
        name: artistName,
        type: typeArray, // 배열로 저장
        category: category, // category 필드 추가
        approved: true,
        registrationSource: "live-lineup", // 등록 경로 구분
      });
    } catch (error) {
      console.error(`아티스트 '${artistName}' 저장 중 오류:`, error);
      // 개별 아티스트 저장 실패는 무시하고 계속 진행
    }
  }
}
